/// <reference types="vite/client" />
import axios, { AxiosResponse } from 'axios';
import { onlineStatusService } from './offline';
import { localStorageService } from './localStorage';

// Use relative path in production (nginx will proxy to backend)
// Use full URL in development
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:3001');

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Список эндпоинтов, которые можно кэшировать
const CACHEABLE_ENDPOINTS = ['/games', '/games/'];

// Список эндпоинтов, которые нужно сохранять в очередь при офлайн
const QUEUEABLE_ENDPOINTS = [
  '/games/:id/base/complete',
  '/games/:id/plates/submit',
  '/games/:id/roulette/submit',
  '/games/:id/regions/complete',
];

function isQueueableEndpoint(url: string, method: string): boolean {
  if (method === 'GET') return false; // GET запросы не сохраняем в очередь
  
  // Проверяем паттерны эндпоинтов
  return QUEUEABLE_ENDPOINTS.some((pattern) => {
    const regex = new RegExp(pattern.replace(':id', '[^/]+'));
    return regex.test(url);
  });
}

function isCacheableEndpoint(url: string): boolean {
  return CACHEABLE_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

// Request interceptor to add auth token and handle offline mode
api.interceptors.request.use(
  async (config) => {
    console.log('[API Request]', config.method, config.url, {
      data: config.data,
      online: onlineStatusService.getStatus(),
    });
    
    const token = localStorage.getItem('auth-storage');
    if (token) {
      try {
        const parsed = JSON.parse(token);
        if (parsed.state?.token) {
          config.headers.Authorization = `Bearer ${parsed.state.token}`;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Если офлайн и это запрос, который можно сохранить в очередь
    if (!onlineStatusService.getStatus() && isQueueableEndpoint(config.url || '', config.method || 'GET')) {
      // Преобразуем заголовки в обычный объект
      const headers: Record<string, string> = {};
      if (config.headers) {
        Object.keys(config.headers).forEach((key) => {
          const value = config.headers[key];
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }

      // Сохраняем запрос в очередь
      const requestId = await localStorageService.addPendingRequest({
        method: config.method?.toUpperCase() || 'POST',
        url: config.url || '',
        data: config.data,
        headers,
      });

      // Помечаем конфиг для обработки в response interceptor
      (config as any).__offlineRequest = true;
      (config as any).__offlineRequestId = requestId;
    }

    // Если офлайн и это GET запрос, пытаемся получить из кэша
    if (!onlineStatusService.getStatus() && config.method?.toUpperCase() === 'GET' && isCacheableEndpoint(config.url || '')) {
      // Извлекаем gameId из URL если есть
      const gameIdMatch = config.url?.match(/\/games\/([^/]+)/);
      if (gameIdMatch) {
        const gameId = gameIdMatch[1];
        const cached = await localStorageService.getCachedGame(gameId);
        if (cached) {
          // Помечаем конфиг для обработки в response interceptor
          (config as any).__cachedData = cached;
        }
      }
    }

    console.log('[API Request] Final config:', {
      url: config.url,
      method: config.method,
      hasAuth: !!config.headers.Authorization,
      offlineRequest: (config as any).__offlineRequest,
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and caching
api.interceptors.response.use(
  async (response: AxiosResponse) => {
    // Кэшируем успешные GET запросы игр
    if (response.config.method?.toUpperCase() === 'GET' && isCacheableEndpoint(response.config.url || '')) {
      const gameIdMatch = response.config.url?.match(/\/games\/([^/]+)/);
      if (gameIdMatch) {
        const gameId = gameIdMatch[1];
        await localStorageService.cacheGame(gameId, response.data);
      }
    }

    return response;
  },
  async (error) => {
    const config = error.config;

    // Обрабатываем офлайн запросы, сохраненные в очередь
    if (config && (config as any).__offlineRequest) {
      return Promise.resolve({
        data: {
          success: true,
          offline: true,
          message: 'Запрос сохранен. Будет отправлен при восстановлении соединения.',
        },
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
      });
    }

    // Обрабатываем кэшированные данные
    if (config && (config as any).__cachedData) {
      return Promise.resolve({
        data: (config as any).__cachedData,
        status: 200,
        statusText: 'OK (Cached)',
        headers: {},
        config,
      });
    }

    // Обрабатываем сетевые ошибки
    if (!error.response && error.request) {
      // Сетевая ошибка - возможно офлайн
      if (!onlineStatusService.getStatus()) {
        // Если это запрос, который можно сохранить в очередь
        if (config && isQueueableEndpoint(config.url || '', config.method || 'GET')) {
          // Преобразуем заголовки в обычный объект
          const headers: Record<string, string> = {};
          if (config.headers) {
            Object.keys(config.headers).forEach((key) => {
              const value = config.headers[key];
              if (typeof value === 'string') {
                headers[key] = value;
              }
            });
          }

          await localStorageService.addPendingRequest({
            method: config.method?.toUpperCase() || 'POST',
            url: config.url || '',
            data: config.data,
            headers,
          });

          return Promise.resolve({
            data: {
              success: true,
              offline: true,
              message: 'Запрос сохранен. Будет отправлен при восстановлении соединения.',
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
          });
        }
      }
    }

    if (error.response?.status === 401) {
      // Clear auth on 401
      localStorage.removeItem('auth-storage');
      window.location.href = '/auth';
    }

    return Promise.reject(error);
  }
);

